import { useState, useEffect } from 'react';
import { api } from '@/lib/api';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  value: boolean;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();

function getCached(archeId: string): boolean | undefined {
  const entry = cache.get(archeId);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    cache.delete(archeId);
    return undefined;
  }
  return entry.value;
}

/**
 * Checks whether an arche is published/available by fetching its metadata.
 * Results are cached with a 5-minute TTL.
 */
export function useArcheAvailability(archeId: string): boolean {
  const [available, setAvailable] = useState(getCached(archeId) ?? false);

  useEffect(() => {
    const cached = getCached(archeId);
    if (cached !== undefined) {
      setAvailable(cached);
      return;
    }

    let cancelled = false;

    api
      .getArcheMetadata(archeId)
      .then(() => {
        cache.set(archeId, { value: true, expiresAt: Date.now() + CACHE_TTL_MS });
        if (!cancelled) setAvailable(true);
      })
      .catch(() => {
        cache.set(archeId, { value: false, expiresAt: Date.now() + CACHE_TTL_MS });
        if (!cancelled) setAvailable(false);
      });

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  return available;
}
